import { CadyAddOn } from '@/lib/cady/cadyEnums'

import type { DeepResolved } from '@/types/content'

import type { BookingAddOnContent } from '@VelocitySquared/sunny16-content-dataobjects-cady/booking'

export enum AddOnsTheme {
  Dark = 'dark',
  Light = 'light'
}

/**
 * Whether a section represents a real catalog-driven add-on (attaches as a
 * cart line item) or a client-synthesized upgrade trigger (click → forced
 * upgrade modal, never attaches). See ADR 011.
 */
export enum AddOnSectionKind {
  ADDON = 'addon',
  UPGRADE_TRIGGER = 'upgrade-trigger'
}

/**
 * Section identifiers that, on Elite-tier sessions, are rendered as
 * client-synthesized upgrade triggers rather than catalog-driven add-ons,
 * mapped to the `CadyAddOn` they represent. Single source of truth shared
 * by:
 * - `bookingCommerce.getAddOnsPageContent` — filters these out of the
 *   catalog `addOns` array and appends synthesized sections in their place
 * - `bookingCommerce.synthesizeEliteHairMakeupTriggers` — iterates to
 *   build the synthesized sections
 * - `AddOnsSelectionView.getUpgradeTargetForAddOn` — looks up the
 *   `CadyAddOn` for the rule resolution
 *
 * See ADR 011.
 */
export const ELITE_TRIGGER_ID_TO_CADY_ADDON: Record<string, CadyAddOn> = {
  HAIR: CadyAddOn.Hair,
  MAKEUP: CadyAddOn.Makeup
}

/** CMS content enriched with runtime pricing from the API. */
export type AddOnSectionEnriched = DeepResolved<BookingAddOnContent> & {
  /**
   * Display string like '+$45.99'. For `ADDON` sections, computed from the
   * API bundle's `pricing.basePrice`. For `TRIGGER` sections, computed from
   * the upgrade-target tier price delta (e.g. Elite → EP).
   */
  addButtonPrice: string
  /** Numeric price value. Same source as `addButtonPrice`. */
  addButtonPriceValue: number
  /** Discriminates real add-ons from client-synthesized upgrade triggers. */
  kind: AddOnSectionKind
}

export interface AddOnsPageContent {
  heading: string
  subtitle: string
  backButtonLabel: string
  ctaLabel: string
  theme: AddOnsTheme
  firstSectionImagePosition?: 'left' | 'right'
  sections: AddOnSectionEnriched[]
  /** Section identifiers that should be auto-selected ('added') when the page loads. */
  preSelectedSectionIds?: string[]
  /** Groups of mutually exclusive section identifiers — selecting one auto-clears the others in the group. */
  mutuallyExclusiveSectionIds?: string[][]
  /** Error message shown when the user tries to continue without a required selection. */
  validationErrorMessage?: string
  /** Label for the "Hair AND Makeup" upgrade button in the hair/makeup toggle. */
  hairAndMakeupUpgradeLabel?: string
  /** Mobile-only label for the upgrade button (shorter, no "(Upgrade)" suffix). */
  hairAndMakeupUpgradeLabelMobile?: string
  /** Price-row badge shown on each Hair-or-Makeup option indicating that the option is included with the bundle. */
  hairMakeupIncludedLabel: string
  /** Toast template shown after an upgrade modal confirm. `{tierName}` is replaced with the target bundle title. */
  upgradeSuccessToastTemplate?: string
}

export enum AddOnSelectionState {
  None = 'none',
  Added = 'added',
  Declined = 'declined'
}
